"use client";

import type { Vehicle, BankTransferInfo } from "@/lib/types";
import type { PriceBreakdown as Breakdown } from "@/lib/pricing-breakdown";
import PriceBreakdownView from "./PriceBreakdown";
import PayButton from "./PayButton";
import { useCompany } from "./CompanyProvider";

interface BookingConfirmationProps {
  vehicle: Vehicle;
  dateFrom: string;
  dateTo: string;
  breakdown: Breakdown;
  bookingId: number;
  paymentMethod: "cash" | "stripe" | "transfer";
  payToken: string;
  bankTransfer?: BankTransferInfo | null;
}

export default function BookingConfirmation({
  vehicle,
  dateFrom,
  dateTo,
  breakdown,
  bookingId,
  paymentMethod,
  payToken,
  bankTransfer,
}: BookingConfirmationProps) {
  const { phone, phoneHref } = useCompany();
  return (
    <div className="bg-green-50 border border-green-200 rounded-xl p-6">
      <div className="flex items-center gap-2 mb-4">
        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
        <h3 className="text-lg font-semibold text-green-900">Rezervacija patvirtinta!</h3>
      </div>

      <div className="space-y-2 text-sm text-green-800 mb-4">
        <p>
          <span className="font-medium">Automobilis:</span> {vehicle.make} {vehicle.model} ({vehicle.year})
        </p>
        <p>
          <span className="font-medium">Laikotarpis:</span> {dateFrom} &mdash; {dateTo}
        </p>
        <p>
          <span className="font-medium">Atsiėmimas:</span> {breakdown.pickup_location.name}
        </p>
        <p>
          <span className="font-medium">Grąžinimas:</span> {breakdown.return_location.name}
        </p>
      </div>

      <div className="bg-white/60 rounded-lg p-3 border border-green-200">
        <PriceBreakdownView breakdown={breakdown} variant="confirmation" />
      </div>

      {paymentMethod === "cash" && (
        <p className="mt-4 text-sm text-green-800 font-medium">
          Apmokėsite atsiimdami automobilį (grynais arba kortele).
        </p>
      )}

      {paymentMethod === "stripe" && (
        <div className="mt-4">
          <p className="text-sm text-amber-800 mb-2">
            Apmokėjimas dar neužbaigtas. Galite apmokėti dabar:
          </p>
          <PayButton bookingId={bookingId} amount={breakdown.total} token={payToken} />
        </div>
      )}

      {paymentMethod === "transfer" && bankTransfer && (
        <div className="mt-4 rounded-lg border border-green-300 bg-white/70 p-4">
          <p className="text-sm font-medium text-green-900 mb-2">
            Pateikite banko pavedimą už rezervaciją ({bankTransfer.amount} €):
          </p>
          <dl className="text-sm text-green-900 space-y-1">
            <div className="flex justify-between gap-3">
              <dt className="text-green-700">Gavėjas</dt>
              <dd className="font-medium text-right">{bankTransfer.recipient}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-green-700">Sąskaita (IBAN)</dt>
              <dd className="font-mono font-medium text-right break-all">{bankTransfer.iban}</dd>
            </div>
            {bankTransfer.bank && (
              <div className="flex justify-between gap-3">
                <dt className="text-green-700">Bankas</dt>
                <dd className="font-medium text-right">{bankTransfer.bank}</dd>
              </div>
            )}
            <div className="flex justify-between gap-3">
              <dt className="text-green-700">Mokėjimo paskirtis</dt>
              <dd className="font-medium text-right">{bankTransfer.reference}</dd>
            </div>
            <div className="flex justify-between gap-3 border-t border-green-200 pt-1 mt-1">
              <dt className="text-green-700">Suma</dt>
              <dd className="font-bold text-right">{bankTransfer.amount} €</dd>
            </div>
          </dl>
          <p className="mt-2 text-xs text-green-700">
            Rezervacija laikoma patvirtinta; gavę pavedimą pažymėsime kaip apmokėtą.
          </p>
        </div>
      )}

      {breakdown.deposit > 0 && (
        <p className="mt-3 text-sm text-green-700">
          Į sumą įskaičiuotas grąžinamas užstatas <span className="font-medium">{breakdown.deposit} €</span> —
          jį grąžinsime banko pavedimu pasibaigus nuomai.
        </p>
      )}

      <p className="mt-4 text-sm text-green-700">
        Susisieksime su Jumis trumpiausiu metu dėl automobilio perdavimo detalių.
        Kilus klausimų, skambinkite{" "}
        <a href={phoneHref} className="underline font-medium">
          {phone}
        </a>
        .
      </p>
    </div>
  );
}
